import React from "react";
import { useGet, usePut } from "../../../../api/useCall";
import t from "../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import { useSources } from "../../../../api/useSources";
import type { Project } from "../../../../api/useProjects";
import Select from "@ui/Select";
import { useSourceDataInput } from "../../../Projects/useSourceDataInput";
import { comparatorBy } from "diagnostics-lib/comparator";
interface P {
  project: Project;
  onRequestClose: () => void;
  preselectedSource?: string;
}

const ChangeDatasourceModalForm = ({ project, onRequestClose, preselectedSource }: P) => {
  const updateProject = usePut("/projects/:projectId(uuid)");

  const sources = useSources();
  const getSourceCompatibility = useGet("/projects/:projectId(uuid)/source-compatibility");
  const [missingSensorTags, setMissingSensorTags] = React.useState<string[] | null>(null); // null = loading

  const {
    placeholder: datasourcePlaceHolder,
    sourceValue: sourceIdInput,
    handleSourceValueChanged,
    isSourceValueDirty,
    handleRollbackSources,
  } = useSourceDataInput([], preselectedSource);

  const sourcesOptions = React.useMemo(
    () =>
      sources
        .map(src => ({
          value: src.id,
          label: src.id,
        }))
        .sort(comparatorBy(s => s.label)),
    [sources],
  );

  React.useEffect(() => {
    if (sourceIdInput === project.sourceId) {
      setMissingSensorTags([]);
      return;
    }

    const currentSourceIdInput = sourceIdInput;
    setMissingSensorTags(null);
    getSourceCompatibility({
      routeParams: { projectId: project.id },
      query: { sourceId: sourceIdInput },
    })
      .then(({ body: { missingSensorTags } }) => {
        if (currentSourceIdInput === sourceIdInput) setMissingSensorTags(missingSensorTags);
      })
      .catch(error => {
        if (currentSourceIdInput === sourceIdInput) setMissingSensorTags([]);
        throw error;
      });
  }, [getSourceCompatibility, project, sourceIdInput]);

  const invalid = missingSensorTags === null || missingSensorTags.length;

  const handleSubmit = React.useCallback(async () => {
    if (invalid) return false;

    const updateProjectData = {
      ...project,
      sourceId: sourceIdInput,
    };

    await updateProject({
      body: updateProjectData,
      routeParams: { projectId: project.id },
    });

    return true;
  }, [invalid, project, updateProject, sourceIdInput]);

  return (
    <ModalForm
      onSubmit={isSourceValueDirty && !invalid ? handleSubmit : false}
      onRequestClose={onRequestClose}
      okButtonName={t("modalForm.button.name.modify")}
    >
      <Select
        filtered
        label={t("projects.modal.update_projects.title.dataSource")}
        onChange={handleSourceValueChanged}
        value={sourceIdInput}
        error={
          missingSensorTags && missingSensorTags.length
            ? t("projects.modal.edit.input.dataSource.error", { missingSensorTags })
            : undefined
        }
        options={sourcesOptions}
        prompt={datasourcePlaceHolder}
        revertable={isSourceValueDirty}
        revert={handleRollbackSources}
        disabled={missingSensorTags === null}
        clearable={false}
      />
    </ModalForm>
  );
};

export default React.memo(ChangeDatasourceModalForm);
